import { Box } from '@material-ui/core';
import { CheckoutSessionStatus, DownPayment, formatMoney, formatNumber } from '@nemuru-eng/js-api-client';

import { CartSummaryDownPayment } from '@/components/modules/_shared/CartSummary/components/CartSummaryDownPayment';
import { CartSummaryData } from '@/components/modules/_shared/CartSummary/types/CartSummaryData';
import { ApplicationStatusChip } from '@/components/modules/Application/components/_shared/components/ApplicationSummary/components/ApplicationStatusChip';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { normalizeSpanishLicensePlate } from '@/utils/zodValidation/validateSpanishLicensePlate';

interface AutoCartSummaryProps {
  cartSummaryData: CartSummaryData;
  downPayment: DownPayment;
  discount?: number;
  checkoutSessionStatus?: CheckoutSessionStatus | undefined;
  strikethrough?: boolean;
  showTitle?: boolean;
}

export const AutoCartSummary = ({
  cartSummaryData,
  downPayment,
  discount,
  checkoutSessionStatus,
  strikethrough,
  showTitle = true,
}: AutoCartSummaryProps) => {
  const autoLine = cartSummaryData.cartSummaryLines[0];

  return (
    <>
      {showTitle && (
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Font variant="h5" color="title">
            <Text strong tId={'AUTO_DETAILS'} />
          </Font>
          {checkoutSessionStatus && (
            <Box position="relative" top="-4px">
              <ApplicationStatusChip checkoutSessionStatus={checkoutSessionStatus} />
            </Box>
          )}
        </Box>
      )}
      <Box display={'flex'} flexDirection={'column'} gridGap={4} mt={showTitle ? 2 : 0}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Font className="overflow-text-ellipsis pr-3" variant={'body2'} strikethrough={strikethrough}>
            <strong>{autoLine.description}</strong>
          </Font>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Font className="overflow-text-ellipsis pr-3" variant={'body2'} strikethrough={strikethrough}>
            <Text tId={'IDENTIFICATION_NUMBER'} />:
          </Font>
          <Font variant={'body2'} strikethrough={strikethrough}>
            {autoLine?.identificationNumber ?? '-'}
          </Font>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Font className="overflow-text-ellipsis pr-3" variant={'body2'} strikethrough={strikethrough}>
            <Text tId={'LICENSE_PLATE_NUMBER'} />:
          </Font>
          <Font variant={'body2'} strikethrough={strikethrough}>
            {autoLine?.plateNumber ? normalizeSpanishLicensePlate(autoLine.plateNumber) : '-'}
          </Font>
        </Box>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Font className="overflow-text-ellipsis pr-3" variant={'body2'} strikethrough={strikethrough}>
            <Text tId={'ODOMETER'} />:
          </Font>
          <Font variant={'body2'} strikethrough={strikethrough}>
            {autoLine?.odometer ? `${formatNumber(autoLine.odometer, 0)} Km` : '-'}
          </Font>
        </Box>
      </Box>
      <CustomDivider margin={2} />
      <Box display={'flex'} flexDirection={'column'} gridGap={4} mt={2}>
        <Box display={'flex'} justifyContent={'space-between'}>
          <Font className="overflow-text-ellipsis pr-3" variant={'body2'} strikethrough={strikethrough}>
            <Text tId={'CASH_PRICE'} />:
          </Font>
          <Font variant={'body2'} strikethrough={strikethrough}>
            {formatMoney(autoLine?.totalPrice, 2)}
          </Font>
        </Box>
        {!!(downPayment.amount.amount >= 0 || discount) && (
          <CartSummaryDownPayment
            cartSummaryData={cartSummaryData}
            downPayment={downPayment}
            discount={discount}
            checkoutSessionStatus={checkoutSessionStatus}
            strikethrough={strikethrough}
          />
        )}
      </Box>
    </>
  );
};
